import { useApolloClient } from "@apollo/client";
import { Box } from "@mui/material";
import { useSnackbar } from "apps/web/src/components/Snackbar/SnackbarContext";
import React, { useState } from "react";
import { useGoalTypesQuery, useResourceSharingOrgGroupsQuery } from "../../../../types";
import { OrgGroupMealPlanTemplates } from "../../../../views/MealPlanTemplates/OrgGroupMealPlanTemplates";
import { OrgMealPlanTemplates } from "../../../../views/MealPlanTemplates/OrgMealPlanTemplates";
import { StaffMealPlanTemplates } from "../../../../views/MealPlanTemplates/StaffMealPlanTemplates";

export type MealPlanTemplateTab = "org" | "orgGroup" | "staff";

export const ROWS_PER_PAGE = 10;

export const MealPlanTemplatesPage = () => {
  const goalTypeResult = useGoalTypesQuery();
  const goalTypes = goalTypeResult.data?.goalTypes || [];

  const orgGroupResult = useResourceSharingOrgGroupsQuery();
  const hasOrgGroupTemplates = (orgGroupResult.data && orgGroupResult.data.resourceSharingOrgGroups.length > 0) ?? false;

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const [tab, setTab] = useState<MealPlanTemplateTab>("org");

  const apolloClient = useApolloClient();
  const { setMessage } = useSnackbar();
  const onCompleteAction = (action: string, name: string, resetPaging: boolean, mealPlanTemplateId?: string) => {
    if (resetPaging) {
      // Evict both org and orgGroup queries, since meal plan template can be transferred between org and org group
      apolloClient.cache.evict({ fieldName: "mealPlanTemplateOffsetConnection", broadcast: true });
      apolloClient.cache.evict({ fieldName: "orgGroupMealPlanTemplateOffsetConnection", broadcast: true });
      // Also evict org group owner query, since the set of owners may have changed
      apolloClient.cache.evict({ fieldName: "orgGroupMealPlanTemplateOwnerCursorConnection", broadcast: true });
    }
    setMessage("success", `Successfully ${action} Meal Plan Template ${name}`);
    setSelectedTemplateId(mealPlanTemplateId || null);
  };

  const onCopiedTemplate = (mealPlanTemplateId: string) => {
    onCompleteAction("copied", "", true);
    setTab("org");
    setSelectedTemplateId(mealPlanTemplateId);
  };

  return (
    <Box sx={{ flex: 1, height: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      {tab === "org" && (
        <OrgMealPlanTemplates
          onChangeTab={setTab}
          onCompleteAction={onCompleteAction}
          onSelectTemplateId={setSelectedTemplateId}
          selectedTemplateId={selectedTemplateId}
          orgGroups={orgGroupResult.data?.resourceSharingOrgGroups || []}
          goalTypes={goalTypes}
          hasOrgGroupTemplates={hasOrgGroupTemplates}
        />
      )}
      {tab === "orgGroup" && (
        <OrgGroupMealPlanTemplates
          onChangeTab={setTab}
          onCompleteAction={onCompleteAction}
          onSelectEditTemplateId={setSelectedTemplateId}
          selectedEditTemplateId={selectedTemplateId}
          goalTypes={goalTypes}
          hasOrgGroupTemplates={hasOrgGroupTemplates}
        />
      )}
      {tab === "staff" && (
        <StaffMealPlanTemplates
          onCopiedTemplate={onCopiedTemplate}
          goalTypes={goalTypes}
          onChangeTab={setTab}
          hasOrgGroupTemplates={hasOrgGroupTemplates}
        />
      )}
    </Box>
  );
};
